import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Bot, MessageCircle, Plus, Send, UserRound } from 'lucide-react'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { ChatMarkdown } from '@/components/chat/ChatMarkdown'
import { useFamily } from '@/hooks'
import {
  useChatConversation,
  useChatConversations,
  useCreateChatConversation,
  useStreamChatMessage,
} from '@/hooks/useChat'
import { cn } from '@/lib/utils'
import type { ChatMessage } from '@/types'

function AssistantAvatar() {
  return (
    <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
      <Bot className="h-4 w-4" />
    </div>
  )
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'USER'
  return (
    <div className={cn('flex gap-3', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && <AssistantAvatar />}
      <div
        className={cn(
          'max-w-[78%] rounded-lg px-4 py-3 text-sm leading-6',
          isUser
            ? 'bg-primary-600 text-white'
            : 'border border-gray-200 bg-surface text-gray-900'
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <ChatMarkdown content={message.content} />
        )}
        <p className={cn('mt-2 text-[11px]', isUser ? 'text-primary-100' : 'text-gray-400')}>
          {new Date(message.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </p>
      </div>
      {isUser && (
        <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
          <UserRound className="h-4 w-4" />
        </div>
      )}
    </div>
  )
}

/** The user's just-sent message, rendered before the server echoes it back. */
function PendingUserBubble({ content }: { content: string }) {
  return (
    <div className="flex justify-end gap-3">
      <div className="max-w-[78%] rounded-lg bg-primary-600 px-4 py-3 text-sm leading-6 text-white">
        <p className="whitespace-pre-wrap">{content}</p>
      </div>
      <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
        <UserRound className="h-4 w-4" />
      </div>
    </div>
  )
}

/** The assistant reply while it streams in (or a typing indicator before
 * the first token arrives). */
function StreamingAssistantBubble({ content }: { content: string }) {
  return (
    <div className="flex gap-3">
      <AssistantAvatar />
      <div className="max-w-[78%] rounded-lg border border-gray-200 bg-surface px-4 py-3 text-sm leading-6 text-gray-900">
        {content ? (
          <>
            <ChatMarkdown content={content} />
            <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse rounded-sm bg-primary-400 align-text-bottom" />
          </>
        ) : (
          <span className="flex items-center gap-1.5 text-gray-500">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:0ms]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:150ms]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:300ms]" />
          </span>
        )}
      </div>
    </div>
  )
}

export function ChatPage() {
  const { selectedFamilyId, selectedFamily } = useFamily()
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const bottomRef = useRef<HTMLDivElement | null>(null)

  const conversationsQuery = useChatConversations(selectedFamilyId)
  const conversations = conversationsQuery.data ?? []
  const activeConversationId = selectedConversationId ?? conversations[0]?.id ?? null

  const conversationQuery = useChatConversation(selectedFamilyId, activeConversationId)
  const createConversation = useCreateChatConversation(selectedFamilyId)
  const stream = useStreamChatMessage(selectedFamilyId)

  const messages = useMemo(
    () => conversationQuery.data?.messages ?? [],
    [conversationQuery.data?.messages]
  )

  // Hide the optimistic user bubble once the persisted copy is in the cache.
  const showPendingUser =
    stream.pendingUserMessage !== null &&
    !messages.some(
      (m) => m.role === 'USER' && m.content === stream.pendingUserMessage
    )

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, stream.isStreaming, stream.streamingContent])

  const handleNewConversation = async () => {
    if (!selectedFamilyId) return
    const conversation = await createConversation.mutateAsync({
      title: 'New care chat',
      state: { family_id: selectedFamilyId },
    })
    setSelectedConversationId(conversation.id)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const message = draft.trim()
    if (!message || !selectedFamilyId || stream.isStreaming || createConversation.isPending) {
      return
    }

    setDraft('')

    // New chats: create the (empty) conversation first — this is fast and
    // never waits on the model — then stream the first message into it.
    let conversationId = activeConversationId
    if (!conversationId) {
      const conversation = await createConversation.mutateAsync({
        state: { family_id: selectedFamilyId },
      })
      setSelectedConversationId(conversation.id)
      conversationId = conversation.id
    }

    await stream.send(conversationId, {
      message,
      state_patch: { family_id: selectedFamilyId },
    })
  }

  if (!selectedFamilyId) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-10 text-center text-sm text-gray-500">
            Select or create a family before using chat.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-[640px] flex-col gap-4 p-4 md:flex-row md:p-6">
      <aside className="w-full shrink-0 md:w-80">
        <Card className="h-full">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Care Chat</CardTitle>
              <p className="mt-1 text-xs text-gray-500">{selectedFamily?.name}</p>
            </div>
            <Button size="icon" variant="outline" onClick={handleNewConversation}>
              <Plus className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {conversationsQuery.isLoading ? (
              <div className="space-y-2">
                <div className="h-12 animate-pulse rounded-md bg-gray-100" />
                <div className="h-12 animate-pulse rounded-md bg-gray-100" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                Start a conversation for care navigation, appointment prep, benefits questions, or family coordination.
              </div>
            ) : (
              conversations.map((conversation) => (
                <button
                  key={conversation.id}
                  onClick={() => setSelectedConversationId(conversation.id)}
                  className={cn(
                    'block w-full rounded-md px-3 py-2 text-left text-sm transition-colors',
                    activeConversationId === conversation.id
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <span className="block truncate font-medium">
                    {conversation.title || 'Untitled chat'}
                  </span>
                  <span className="mt-1 block truncate text-xs text-gray-500">
                    {conversation.last_message?.content ?? 'No messages yet'}
                  </span>
                </button>
              ))
            )}
          </CardContent>
        </Card>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
        <div className="border-b border-gray-200 bg-surface px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 text-primary-600">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-gray-900">
                {conversationQuery.data?.title || 'Healthcare advocacy help'}
              </h1>
              <p className="text-xs text-gray-500">
                Family-aware guidance with health, finance, alert, and member context when permitted.
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-5">
          {messages.length === 0 && !stream.isStreaming && !showPendingUser ? (
            <div className="mx-auto mt-16 max-w-xl text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <Bot className="h-6 w-6" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-gray-900">
                Ask about navigating care
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                Try appointment prep, benefits paperwork, next-step planning after an alert, or how to coordinate tasks across family members.
              </p>
            </div>
          ) : (
            <div className="mx-auto flex max-w-3xl flex-col gap-4">
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
              {showPendingUser && (
                <PendingUserBubble content={stream.pendingUserMessage!} />
              )}
              {stream.isStreaming && (
                <StreamingAssistantBubble content={stream.streamingContent} />
              )}
              {stream.error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {stream.error}{' '}
                  <button onClick={stream.clearError} className="font-medium underline">
                    Dismiss
                  </button>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="border-t border-gray-200 bg-surface p-4">
          <div className="mx-auto flex max-w-3xl items-end gap-3">
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  event.currentTarget.form?.requestSubmit()
                }
              }}
              placeholder="Ask for help with appointments, benefits, alerts, or family coordination..."
              rows={2}
              className="min-h-[52px] flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <Button type="submit" size="icon" disabled={!draft.trim() || stream.isStreaming}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </form>
      </main>
    </div>
  )
}
